/**
 * 10.6M — MISSÃO DE TERRITÓRIO (o que abre a porta do muro).
 *
 * O Thiago chama cada TERRITÓRIO de "área" — são 51. Antes a porta abria só por contagem de
 * abates; agora cada território tem a SUA MISSÃO, e é ela que libera a passagem.
 *
 * A missão é derivada do próprio território, então não precisa de conteúdo escrito à mão
 * para os 51 e nunca fica desalinhada com quem mora lá:
 *   · CAÇAR    — abater N monstros DALI (N cresce com o tier);
 *   · CAMPEÃO  — derrotar o campeão, nos territórios que têm um;
 *   · BAÚ      — abrir o baú, nos territórios que têm um dentro.
 *
 * Um território fica liberado se for o da base ou se um vizinho com a missão CUMPRIDA
 * encostar nele: a frente de conquista avança em mancha a partir da base, sem pular etapa.
 *
 * Estado puro: sem DOM, sem three.js.
 */
import { GameConfig, type TerritoryDef } from "../config/GameConfig";

export type TerrTaskKind = "hunt" | "champion" | "chest";

export interface TerrTask {
  kind: TerrTaskKind;
  progress: number;
  target: number;
}

export interface TerritoryProgressSnapshot {
  kills: Record<string, number>;
  champions: Record<string, number>;
  chests: Record<string, number>;
}

/** O território tem um baú dentro? (posições dos baús são fixas na config) */
function chestsIn(def: TerritoryDef, cx: number, cz: number, halfW: number, halfH: number): number {
  let n = 0;
  for (const c of GameConfig.chests.world) {
    if ((c as { region?: string }).region !== def.regionId) continue;
    if (Math.abs(c.x - cx) <= halfW && Math.abs(c.z - cz) <= halfH) n++;
  }
  return n;
}

export class TerritoryProgress {
  private readonly kills: Record<string, number> = {};
  private readonly champions: Record<string, number> = {};
  private readonly chests: Record<string, number> = {};
  /** geometria por território (preenchida pelo TerritoryGates ao montar a região) */
  private readonly rects = new Map<string, { cx: number; cz: number; halfW: number; halfH: number }>();

  setGeometry(id: string, cx: number, cz: number, halfW: number, halfH: number): void {
    this.rects.set(id, { cx, cz, halfW, halfH });
  }

  /**
   * 10.6N — BAÚ JÁ ABERTO CONTA. Baú não renasce no save: quem já tinha aberto o da clareira
   * ficava com a missão do território impossível ("abrir o baú" sem baú para abrir). Ao
   * carregar, todo baú do território que já está aberto marca o objetivo como cumprido.
   */
  seedOpenedChests(regionId: string, isOpen: (chestId: string) => boolean): void {
    for (const [id, r] of this.rects) {
      let opened = 0;
      for (const c of GameConfig.chests.world) {
        if ((c as { region?: string }).region !== regionId) continue;
        if (Math.abs(c.x - r.cx) > r.halfW || Math.abs(c.z - r.cz) > r.halfH) continue;
        if (isOpen(c.id)) opened++;
      }
      if (opened > 0) this.chests[id] = Math.max(this.chests[id] ?? 0, opened);
    }
  }

  /** 10.6N — zera SÓ as missões de território (o resto do progresso fica). */
  resetMissions(): void {
    for (const k of Object.keys(this.kills)) delete this.kills[k];
    for (const k of Object.keys(this.champions)) delete this.champions[k];
    for (const k of Object.keys(this.chests)) delete this.chests[k];
  }

  /** 10.9HS — Quantos abates a missão daquele pedaço exige, por TIER (régua: 20/34/48/62/76). */
  huntTarget(def: TerritoryDef): number {
    const c = GameConfig.territories;
    const t = Math.min(c.huntTargetByTier.length, Math.max(1, def.tier)) - 1;
    return c.huntTargetByTier[t] ?? c.huntTargetByTier[0];
  }

  /** 10.9HS — nível mínimo para a porta deste pedaço abrir: base por TIER, crescendo composto
   *  por PROFUNDIDADE (cada volta relouqueia o mapa — ver Game.onAreaCompleted). O tier mais alto
   *  sobe mais rápido em número absoluto (mesma taxa %, base maior) e persegue a régua de tier de
   *  equipamento (Nv20…1000 — Rarity.ts) ao longo de dezenas de voltas; travado em 999. */
  gateLevel(def: TerritoryDef, depth = 1): number {
    if (def.home) return 1; // a base nunca trava por nível
    const c = GameConfig.territories;
    const byTier = c.gateLevelByTier;
    const base = byTier[Math.min(byTier.length, Math.max(1, def.tier)) - 1] ?? 1;
    const grown = base * Math.pow(1 + c.gateLevelDepthGrowth, Math.max(0, depth - 1));
    return Math.min(999, Math.round(grown));
  }

  /** A MISSÃO do território: 1 a 3 objetivos, montados a partir do próprio pedaço. */
  tasks(def: TerritoryDef): TerrTask[] {
    const out: TerrTask[] = [{ kind: "hunt", progress: this.kills[def.id] ?? 0, target: this.huntTarget(def) }];
    if (def.champion) out.push({ kind: "champion", progress: this.champions[def.id] ?? 0, target: 1 });
    const r = this.rects.get(def.id);
    const nChest = r ? chestsIn(def, r.cx, r.cz, r.halfW, r.halfH) : 0;
    if (nChest > 0) out.push({ kind: "chest", progress: this.chests[def.id] ?? 0, target: 1 });
    return out;
  }

  /** Missão do território cumprida? É isto que abre a porta. */
  cleared(def: TerritoryDef): boolean {
    return this.tasks(def).every((t) => t.progress >= t.target);
  }

  /** O primeiro objetivo em aberto (para a porta dizer o que falta). */
  nextTask(def: TerritoryDef): TerrTask | null {
    return this.tasks(def).find((t) => t.progress < t.target) ?? null;
  }

  /** Registra um abate comum. Devolve true se a missão ACABOU de ser cumprida. */
  addKill(def: TerritoryDef | null | undefined): boolean {
    if (!def) return false;
    const was = this.cleared(def);
    this.kills[def.id] = (this.kills[def.id] ?? 0) + 1;
    return !was && this.cleared(def);
  }

  /** Registra a queda do campeão daquele território. */
  addChampion(def: TerritoryDef | null | undefined): boolean {
    if (!def || !def.champion) return false;
    const was = this.cleared(def);
    this.champions[def.id] = 1;
    return !was && this.cleared(def);
  }

  /** Registra o baú aberto naquele território. */
  addChest(def: TerritoryDef | null | undefined): boolean {
    if (!def) return false;
    const was = this.cleared(def);
    this.chests[def.id] = (this.chests[def.id] ?? 0) + 1;
    return !was && this.cleared(def);
  }

  /**
   * Liberado para entrar? Base sempre; os outros por vizinhança com missão cumprida.
   * 10.7V — NÍVEL + MISSÃO JUNTOS: um pedaço só entra na mancha se o Caçador tiver o nível
   * mínimo dele (gateLevel). Como a frente avança em mancha, ficar sem nível para um pedaço
   * trava tudo que vem depois — o que impede varrer o mapa cedo demais.
   */
  isOpen(def: TerritoryDef, all: readonly TerritoryDef[], playerLevel = Infinity, depth = 1): boolean {
    if (def.home) return true;
    const home = all.find((t) => t.home) ?? all[0];
    if (!home) return true;
    const open = new Set<string>([home.id]);
    for (let pass = 0; pass < all.length; pass++) {
      let grew = false;
      for (const t of all) {
        if (open.has(t.id)) continue;
        if (playerLevel < this.gateLevel(t, depth)) continue; // trava de nível do pedaço
        const ok = all.some(
          (n) => open.has(n.id) && this.cleared(n) && Math.abs(n.col - t.col) + Math.abs(n.row - t.row) === 1,
        );
        if (ok) {
          open.add(t.id);
          grew = true;
        }
      }
      if (!grew) break;
    }
    return open.has(def.id);
  }

  snapshot(): TerritoryProgressSnapshot {
    return { kills: { ...this.kills }, champions: { ...this.champions }, chests: { ...this.chests } };
  }

  load(data: TerritoryProgressSnapshot | undefined): void {
    const fill = (dst: Record<string, number>, src: Record<string, number> | undefined): void => {
      for (const k of Object.keys(dst)) delete dst[k];
      for (const [k, v] of Object.entries(src ?? {})) {
        const n = Math.floor(Number(v) || 0);
        if (n > 0) dst[k] = n;
      }
    };
    fill(this.kills, data?.kills);
    fill(this.champions, data?.champions);
    fill(this.chests, data?.chests);
  }

  reset(): void {
    this.load(undefined);
  }
}
